import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/common/ui-bits";
import { EntryForm } from "@/components/entry/EntryForm";
import { api } from "@/lib/api";
import { money, todayNassau } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ScanButton, ScannedPhotoBar } from "@/components/scan/Scan";

const fromScan = (r) => {
  const f = r.fields;
  const today = todayNassau();
  const desc = [f.bill_number && `Bill # ${f.bill_number}`, f.account_number && `Acct ${f.account_number}`, f.due_date && `due ${f.due_date}`].filter(Boolean).join(" · ");
  const form = { type: "expense", date: f.bill_date && f.bill_date <= today ? f.bill_date : today, amount: f.amount || "", category_id: f.category_id || "",
    vendor: f.vendor || "", description: desc, payment_method: "cash" };
  const ai = new Set([f.amount && "amount", f.bill_date && f.bill_date <= today && "date", f.category_id && "category_id", f.vendor && "vendor"].filter(Boolean));
  return { form, ai };
};

export default function AddEntryPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [last, setLast] = useState(null);
  const [scan, setScan] = useState(null);
  const [sent, setSent] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const pre = scan ? fromScan(scan) : null;
  const reset = () => { setScan(null); setFormKey((k) => k + 1); };
  const onScan = (r) => { setScan(r); setSent(false); setLast(null); setFormKey((k) => k + 1); };
  const save = async (payload) => {
    if (scan && !isAdmin) {
      const f = scan.fields;
      await api.post(`/scans/${scan.scan_id}/submit`, { fields: { ...payload, bill_number: f.bill_number, account_number: f.account_number,
        due_date: f.due_date, period_start: f.period_start, period_end: f.period_end, bill_date: f.bill_date } });
      setSent(true);
      toast.success("Sent for admin review");
      return reset();
    }
    const { data } = await api.post("/transactions", { ...payload, photo_id: scan?.photo_id || null });
    setLast(data); setSent(false);
    toast.success("Entry saved");
    if (scan) reset();
  };
  return (
    <div className="max-w-xl mx-auto">
      <PageHeader eyebrow="Record money in or out" title="Add Entry" testId="add-entry-title" />
      {last && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 fade-up" data-testid="add-entry-success">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
          <span className="min-w-0">Saved {money(last.amount_cents)} · {last.category_name}. <Link to="/my-entries" className="font-semibold underline" data-testid="add-entry-view-mine-link">View my entries</Link></span>
        </div>
      )}
      {sent && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 fade-up" data-testid="add-entry-sent-for-review">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-sky-600" />Sent for admin review
        </div>
      )}
      <div className="surface p-4 sm:p-6 mb-4 space-y-3 fade-up">
        <ScanButton purpose="entry" onResult={onScan} testPrefix="entry-scan" />
        <ScannedPhotoBar scan={scan} onClear={reset} testPrefix="entry-scan" />
      </div>
      <div className="surface p-4 sm:p-6 fade-up">
        <EntryForm key={formKey} initial={pre?.form} ai={pre?.ai} onSubmit={save} stickySubmit
          submitLabel={scan && !isAdmin ? "Send for review" : "Save entry"} />
      </div>
    </div>
  );
}
